import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Reveal } from '@/components/Reveal';

type Capability = { icon: SvgIconComponent; label: string; note: string };

// Real, executable platform surfaces that don't (yet) have their own top-level module page —
// the depth underneath the six-plus pillars above. Each graduates onto its own page as it earns
// top-level billing; keeping them here beats either hiding them or overstating them individually.
const capabilities: Capability[] = [
  { icon: ReceiptLongOutlinedIcon, label: 'Billing & recurring contracts', note: 'Recurring invoicing tied to the same client and contract record' },
  { icon: InsightsOutlinedIcon, label: 'vCIO planning & reporting', note: 'Strategic reviews built from live ticket, device, and security data' },
  { icon: MailOutlineIcon, label: 'Mail & calendar', note: 'Inbox and scheduling in the same tenant-scoped record' },
  { icon: SmartToyOutlinedIcon, label: 'Automation & remediation', note: 'Playbooks and scripted remediation beyond one-off tickets' },
  { icon: VerifiedUserOutlinedIcon, label: 'Signed endpoint execution', note: 'Ed25519-signed jobs behind a default-deny capability registry' },
  { icon: MenuBookOutlinedIcon, label: 'IT documentation', note: 'Runbooks and network docs linked to the device and client' },
  { icon: StorefrontOutlinedIcon, label: 'Vendor & procurement', note: 'PO matching and vendor invoice reconciliation' },
  { icon: DashboardOutlinedIcon, label: 'Executive dashboards', note: 'Board-ready rollups sourced from the same live records' },
  { icon: BadgeOutlinedIcon, label: 'Identity integrations', note: 'SSO and device-identity tie-ins beyond local accounts' },
  { icon: RocketLaunchOutlinedIcon, label: 'Client onboarding foundations', note: 'Structured intake for a new client or a new SaaS integration' },
  { icon: GavelOutlinedIcon, label: 'Audit & governance operations', note: 'Evidence collection and control tracking behind the compliance module' },
  { icon: CalendarMonthOutlinedIcon, label: 'Scheduling & business hours', note: 'SLA clocks and dispatch aware of real coverage windows' },
];

export function CapabilityMap(): JSX.Element {
  return (
    <section className="border-t border-white/10 py-16 md:py-24">
      <div className="container-content">
        <Reveal>
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
              Beneath the modules
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[30px]">
              More platform than six pillars.
            </h2>
            <p className="mt-4 text-white/70">
              Each module above is a buying category, not the full architecture. Underneath it,
              Nexus already carries real, executable surfaces like these — they don&apos;t have a
              top-level page yet because we&apos;d rather give a module its own page once it&apos;s
              earned one, not because the capability doesn&apos;t exist.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {capabilities.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.label} delay={(i % 6) * 60}>
                <div className="group flex h-full items-start gap-3.5 rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                    <Icon fontSize="small" />
                  </div>
                  <div>
                    <h3 className="font-display text-[15px] font-semibold text-nexus-ink">{c.label}</h3>
                    <p className="mt-1 text-xs text-white/55">{c.note}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="mt-8 text-center text-xs text-white/40">
          Not an exhaustive list, and not a promise every row above is GA-polished today — see the{' '}
          <Link to="/roadmap" className="text-white/50 underline hover:text-white/70">
            roadmap
          </Link>{' '}
          for build phase, or ask us directly about any specific capability&apos;s current state.
        </p>
      </div>
    </section>
  );
}
